import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiUsers,
  FiBriefcase,
  FiDollarSign,
  FiFileText,
  FiCreditCard,
  FiFolderPlus,
  FiUserCheck,
  FiSettings,
  FiSliders,
  FiList,
  FiHome,
  FiClock,
  FiBookOpen,
  FiFlag,
  FiBarChart2,
} from "react-icons/fi";
import { useAuth } from "../hooks/useAuth";
import { MAIN_MENUS, ADMIN_MENUS, USER_GUIDE_MENU } from "../utils/menuConfig";

const ICONS = {
  dashboard: FiHome,
  consultants: FiUsers,
  missions: FiFlag,
  contracts: FiFileText,
  clients: FiBriefcase,
  vendors: FiUserCheck,
  timesheets: FiClock,
  invoices: FiFileText,
  payments: FiDollarSign,
  finance: FiFolderPlus,
  expenses: FiCreditCard,
  history: FiBarChart2,
  templates: FiFileText,
  settings: FiSettings,
  options: FiSliders,
  logs: FiList,
  userGuide: FiBookOpen,
};

export default function Sidebar({ open, mobileOpen = false, onClose, isMobile = false }) {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const normalizedRole = String(user?.role || "").toLowerCase();
  const isSuperUser = normalizedRole === "superuser";
  const hiddenMenus = new Set(user?.hidden_menus || []);

  // On mobile the drawer always shows full labels; on desktop `open` controls
  // expanded (250px) vs collapsed (64px).
  const expanded = open || mobileOpen;

  const isHidden = (menu) => {
    if (isSuperUser) return false;
    return hiddenMenus.has(menu.key);
  };

  const roleAllowed = (menu) => !menu.roles || menu.roles.includes(normalizedRole);

  const renderLink = (menu) => {
    const Icon = ICONS[menu.key] || FiHome;
    return (
      <NavLink
        key={menu.to}
        to={menu.to}
        title={!expanded ? t(menu.labelKey) : undefined}
        onClick={() => {
          if (menu.to === "/consultants") {
            window.dispatchEvent(new CustomEvent("consultants:show-list"));
          }
          if (onClose) onClose();
        }}
        className={({ isActive }) =>
          `flex items-center py-2.5 text-sm transition-colors relative overflow-hidden rounded-md ${
            expanded ? "gap-3 px-4" : "justify-center px-0"
          } ${
            isActive
              ? "bg-adminlte-primary/20 text-white border-l-[3px] border-adminlte-primary"
              : "text-white/70 hover:bg-sidebar-hover hover:text-white border-l-[3px] border-transparent"
          }`
        }
      >
        <Icon size={16} className="flex-shrink-0" />
        {expanded && <span className="flex-1">{t(menu.labelKey)}</span>}
      </NavLink>
    );
  };

  const mainVisible = MAIN_MENUS.filter((menu) => roleAllowed(menu) && !isHidden(menu));
  const adminVisible = ADMIN_MENUS.filter((menu) => roleAllowed(menu) && !isHidden(menu));
  const showUserGuide = !isHidden(USER_GUIDE_MENU);
  const sections = ["overview", "directory", "delivery", "billing", "operations", "system"];
  const visibleMenus = [...mainVisible, ...adminVisible];

  return (
    <aside
      aria-hidden={isMobile ? String(!mobileOpen) : "false"}
      className={`fixed top-0 left-0 h-full bg-sidebar-dark text-white transition-all duration-300 flex flex-col overflow-hidden z-40 md:z-20 w-[250px] ${
        open ? "md:w-[250px]" : "md:w-[64px]"
      } ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
    >
      {/* Brand */}
      <div className="h-14 flex items-center bg-sidebar-darker flex-shrink-0 overflow-hidden px-3">
        <span className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 grid place-items-center text-white text-xs font-bold flex-shrink-0">
          CCI
        </span>
        {expanded && (
          <span className="ml-2.5 text-[15px] font-bold tracking-wide whitespace-nowrap text-white">
            Career Compass
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        {sections.map((section) => {
          const sectionMenus = visibleMenus.filter((menu) => menu.section === section);
          if (!sectionMenus.length) return null;
          return (
            <div key={section} className="sidebar-section">
              {expanded && <p className="sidebar-section-title">{t(`sidebar.sections.${section}`)}</p>}
              {sectionMenus.map(renderLink)}
            </div>
          );
        })}
      </nav>

      {/* User Guide — visible to all */}
      {showUserGuide && (
        <div className="border-t border-white/10 py-2">
          {renderLink(USER_GUIDE_MENU)}
        </div>
      )}
    </aside>
  );
}
